import React, { useEffect, useState } from 'react'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import * as registrationsService from '../../services/registrationsService'

const { FiX, FiCheckCircle } = FiIcons

const initialForm = {
  fullName: '',
  email: '',
  phone: '',
  invitedBy: ''
}

export default function WalkInSignUpModal({
  isOpen,
  onClose,
  eventId,
  eventAgentId,
  authOptions,
  onRegistered
}) {
  const [form, setForm] = useState(initialForm)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [successName, setSuccessName] = useState(null)

  useEffect(() => {
    if (!isOpen) return
    setForm(initialForm)
    setError(null)
    setSuccessName(null)
    setSubmitting(false)
  }, [isOpen])

  if (!isOpen) return null

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!eventId) return

    const fullName = form.fullName.trim()
    const email = form.email.trim()
    if (!fullName || !email) {
      setError('Full name and email are required.')
      return
    }

    setSubmitting(true)
    setError(null)

    const { data: registration, error: registerError } =
      await registrationsService.registerWalkIn(
        {
          eventId,
          agentId: eventAgentId ?? null,
          fullName,
          email,
          phone: form.phone.trim() || null,
          invitedBy: form.invitedBy.trim() || null
        },
        authOptions
      )

    if (registerError) {
      setSubmitting(false)
      setError(registerError.message || 'Unable to register walk-in.')
      if (registration) {
        onRegistered?.(registration)
      }
      return
    }

    setSubmitting(false)
    setSuccessName(fullName)
    onRegistered?.(registration)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Walk-in Sign Up</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <SafeIcon icon={FiX} className="h-5 w-5" />
          </button>
        </div>

        {successName ? (
          <div className="px-6 py-8 text-center">
            <SafeIcon icon={FiCheckCircle} className="mx-auto mb-3 h-12 w-12 text-green-500" />
            <p className="text-lg font-medium text-gray-900">{successName}</p>
            <p className="mt-1 text-sm text-gray-600">Registered and checked in.</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
            {error && (
              <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="walkin-name">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                id="walkin-name"
                type="text"
                value={form.fullName}
                onChange={handleChange('fullName')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                autoComplete="name"
                required
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="walkin-email">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="walkin-email"
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                autoComplete="email"
                required
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="walkin-phone">
                Phone
              </label>
              <input
                id="walkin-phone"
                type="tel"
                value={form.phone}
                onChange={handleChange('phone')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                autoComplete="tel"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="walkin-invited">
                Who Invited <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <input
                id="walkin-invited"
                type="text"
                value={form.invitedBy}
                onChange={handleChange('invitedBy')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-100"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {submitting ? 'Saving…' : 'Register & Check In'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
